import React from "react";
import {
  Card,
  CardContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Button,
  Box,
  Grid,
  TextField,
  IconButton,
  Divider,
  Switch,
  FormControlLabel,
  Tooltip,
  Typography,
} from "@mui/material";
import Label from "../../../../components/Label/Label";
import "./XBarChartConfigs.scss";
import { limitTypeOptions, ruleOptions } from "../constants";
import RuleSelector from "../controlChart/RuleSelector";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";

const XBarChartConfigs = ({
  unitOfOperationId,
  updateState,
  handleGenerate,
  configState,
  onClickClearCurrentConfig,
  configBatchList,
  configSetIds,
  disableAllFields,
  setShowConfigPanel,
  setSelectedTab,
}) => {
  const onChangeControlLimit = (e) => {
    const value = e.target.value;
    updateState("controlLimitType", value);
    if (value !== "User Defined") {
      updateState("lowerControlLimit", "");
      updateState("upperControlLimit", "");
    }
    if (value !== "User Preset") {
      updateState("controlLimitSetId", "");
    }
  };

  const isApplyDisabled = () => {
    // Control: User Preset → must select set
    const isControlPresetInvalid =
      configState.controlLimitType === "User Preset" &&
      !configState.controlLimitSetId;

    // Control: User Defined → must enter at least one (lower/upper)
    const isControlDefinedInvalid =
      configState.controlLimitType === "User Defined" &&
      !(configState.lowerControlLimit && configState.upperControlLimit);

    return (
      !unitOfOperationId || isControlPresetInvalid || isControlDefinedInvalid
    );
  };

  return (
    <Box className="plot-config-wrapper">
      <Box className="scrollable-config">
        {/* Control Limit */}
        <Card elevation={0} className="filter-card plot-config-card">
          <CardContent className="toggle-section">
            <Typography variant="caption" sx={{ pb: 0.5, color: "#c62828" }}>
              Please generate the graph before configuring Limits.
            </Typography>
            <Box>
              <Label text="Control Limit" fontWeight="500" />
              <FormControl
                fullWidth
                size="small"
                className="dropdown-field"
                disabled={disableAllFields}
                error={
                  configState.controlLimitType === "User Preset" &&
                  !configState.controlLimitSetId
                }
              >
                <InputLabel>Select Limit</InputLabel>
                <Select
                  value={configState.controlLimitType}
                  onChange={
                    (e) => onChangeControlLimit(e)
                    // updateState("controlLimitType", e.target.value)
                  }
                  label="Select Limit"
                >
                  {limitTypeOptions?.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* User Preset Set */}
              {configState.controlLimitType === "User Preset" && (
                <FormControl
                  fullWidth
                  size="small"
                  sx={{ mt: 1 }}
                  disabled={disableAllFields}
                  error={!configState.controlLimitSetId}
                >
                  <InputLabel id="control-site-label">Select Set</InputLabel>
                  <Select
                    labelId="control-site-label"
                    value={configState.controlLimitSetId || ""}
                    onChange={(e) =>
                      updateState("controlLimitSetId", e.target.value)
                    }
                    label="Select Set"
                  >
                    {Array.isArray(configSetIds?.control_limit_options) &&
                    configSetIds?.control_limit_options.length > 0 ? (
                      configSetIds.control_limit_options.map((site) => (
                        <MenuItem
                          key={site?.stat_limit_id}
                          value={site?.stat_limit_id}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              width: "100%",
                            }}
                          >
                            <Label text={site?.set_id} />
                            <Tooltip
                              arrow
                              placement="right"
                              title={
                                <Box
                                  sx={{
                                    borderRadius: 2,
                                    boxShadow:
                                      "0 4px 10px rgba(182, 180, 180, 0.15)",
                                    p: 2,
                                    backgroundColor: "#fff",
                                    minWidth: 220,
                                  }}
                                >
                                  <Label
                                    text="Control Limits"
                                    fontWeight="600"
                                    fontSize="14px"
                                    color="#1976d2"
                                  />
                                  <Divider sx={{ my: 1 }} />
                                  <Box
                                    sx={{
                                      display: "grid",
                                      gridTemplateColumns: "100px 1fr",
                                      rowGap: 0.5,
                                    }}
                                  >
                                    <Label text="UCL:" fontWeight="500" />
                                    <Label text={site.upper_control ?? "N/A"} />
                                    <Label text="LCL:" fontWeight="500" />
                                    <Label text={site.lower_control ?? "N/A"} />
                                  </Box>
                                </Box>
                              }
                            >
                              <IconButton
                                size="small"
                                sx={{
                                  p: 0.5,
                                  color: "#1976d2",
                                  "&:hover": {
                                    color: "#0d47a1",
                                    backgroundColor: "rgba(25, 118, 210, 0.08)",
                                  },
                                }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <InfoOutlinedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem disabled>
                        <Label text="No sets available" />
                      </MenuItem>
                    )}
                  </Select>
                </FormControl>
              )}

              {/* User Defined Limits */}
              {configState.controlLimitType === "User Defined" && (
                <Grid container spacing={0.5} pt={1} alignItems="center">
                  <Grid item xs={6}>
                    <TextField
                      type="number"
                      label="Lower Limit"
                      size="small"
                      fullWidth
                      value={configState.lowerControlLimit}
                      onChange={(e) =>
                        updateState("lowerControlLimit", e.target.value)
                      }
                      disabled={disableAllFields}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      type="number"
                      label="Upper Limit"
                      size="small"
                      fullWidth
                      value={configState.upperControlLimit}
                      onChange={(e) =>
                        updateState("upperControlLimit", e.target.value)
                      }
                      disabled={disableAllFields}
                    />
                  </Grid>
                  {!(
                    configState.lowerControlLimit &&
                    configState.upperControlLimit
                  ) &&
                    configState.controlLimitType === "User Defined" && (
                      <Grid item xs={12} sx={{ pt: "8px !important" }}>
                        <Box sx={{ color: "error.main", ml: "8px" }}>
                          <Label
                            fontSize="12px"
                            color="inherit"
                            text="Enter the lower and upper control limit values."
                          />
                        </Box>
                      </Grid>
                    )}
                </Grid>
              )}
            </Box>
          </CardContent>
        </Card>

        {/* Select Batch */}
        <Card elevation={0} className="filter-card plot-config-card">
          <CardContent className="toggle-section">
            <Box>
              <Label text="Select Batch" fontWeight="500" />
              <FormControl
                fullWidth
                size="small"
                className="dropdown-field"
                disabled={disableAllFields}
              >
                <InputLabel>Select Batch</InputLabel>
                <Select
                  value={configState.selectedBatchId || ""}
                  onChange={(e) =>
                    updateState("selectedBatchId", e.target.value)
                  }
                  label="Select Limit"
                >
                  {Array.isArray(configBatchList) &&
                  configBatchList.length > 0 ? (
                    configBatchList.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled>
                      <Label text="No Batch ID" />
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
            </Box>
          </CardContent>
        </Card>
        <Card elevation={0} className="filter-card plot-config-card">
          <CardContent className="toggle-section">
            <FormControlLabel
              sx={{ pl: "8px", pr: "8px" }}
              control={
                <Switch
                  checked={configState.show_grid}
                  onChange={(e) => updateState("show_grid", e.target.checked)}
                />
              }
              label={<Label text="Show Grid" fontWeight="500" />}
              disabled={disableAllFields}
            />
          </CardContent>
        </Card>

        {/* Nelson Rules */}
        <Card elevation={0} className="filter-card plot-config-card">
          <CardContent className="rule-section">
            <Label text="Nelson Rules" fontWeight="500" />
            <RuleSelector
              disabled={disableAllFields}
              ruleOptions={ruleOptions}
              selectedRules={configState.selectedRules}
              setSelectedRules={(val) => updateState("selectedRules", val)}
            />
          </CardContent>
        </Card>
      </Box>

      {/* Sticky Footer */}
      <Box className="plot-footer-sticky">
        <Card elevation={0} className="filter-card plot-footer-btn-card">
          <CardContent>
            <Box display="flex" justifyContent="flex-end" gap={1}>
              <Tooltip title="Previous">
                <IconButton
                  onClick={() => setSelectedTab("batchFilters")}
                  size="small"
                  sx={{
                    backgroundColor: "primary.main",
                    color: "#fff",
                    "&:hover": {
                      backgroundColor: "primary.dark",
                    },
                    borderRadius: "8px",
                    px: 1,
                    py: 0.6,
                  }}
                >
                  <ArrowBackIosNewIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Button
                variant="outlined"
                className="close-btn"
                onClick={() => setShowConfigPanel(false)}
              >
                Close
              </Button>
              <Button
                variant="outlined"
                onClick={onClickClearCurrentConfig}
                disabled={disableAllFields}
              >
                Clear Config
              </Button>
              <Button
                variant="contained"
                onClick={handleGenerate}
                disabled={isApplyDisabled() || disableAllFields}
              >
                Apply
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default XBarChartConfigs;
